import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api, errMsg, fmtDate } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { FileText, Images, ListChecks, ChevronRight, Loader2, ShieldCheck, User } from "lucide-react";
import { toast } from "sonner";
import { istNeu } from "@/components/portal/ZuordnungPicker";

export default function LieferantPortalPage() {
  const [dokumente, setDokumente] = useState([]);
  const [gallerien, setGallerien] = useState([]);
  const [umfragen, setUmfragen] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [d, g, u] = await Promise.all([
          api.get("/documents").then((r) => r.data).catch(() => []),
          api.get("/galleries").then((r) => r.data).catch(() => []),
          api.get("/surveys").then((r) => r.data).catch(() => []),
        ]);
        setDokumente(d); setGallerien(g); setUmfragen(u);
      } catch (e) { toast.error(errMsg(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  if (loading) {
    return <div className="flex justify-center py-20"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const offeneUmfragen = umfragen.filter((s) => !s.bereits_beantwortet);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold" style={{ fontFamily: "var(--font-heading)" }}>Molkerei-Portal</h1>
        <p className="text-sm text-muted-foreground">
          Aktuelle Informationen, Galerien und Umfragen der Molkerei Seifried.
        </p>
      </div>

      {/* Umfragen */}
      {offeneUmfragen.length > 0 && (
        <Card className="border-primary/30" data-testid="portal-umfragen-card">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <ListChecks className="h-5 w-5 text-primary" /> Offene Umfragen ({offeneUmfragen.length})
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {offeneUmfragen.map((s) => (
              <Link key={s.id} to={`/portal/umfrage/${s.id}`}
                data-testid={`portal-umfrage-${s.id}`}
                className="flex items-center gap-3 rounded-lg border bg-card p-3 hover:bg-secondary/50 transition-colors">
                <div className="flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <span className="font-medium">{s.titel}</span>
                    {s.modus === "anonym"
                      ? <Badge variant="outline" className="text-[10px]"><ShieldCheck className="h-3 w-3 mr-1" /> anonym</Badge>
                      : <Badge variant="outline" className="text-[10px]"><User className="h-3 w-3 mr-1" /> namentlich</Badge>}
                  </div>
                  {s.beschreibung && <p className="text-xs text-muted-foreground mt-0.5 line-clamp-1">{s.beschreibung}</p>}
                  <p className="text-xs text-muted-foreground mt-1">
                    {s.fragen?.length || 0} Fragen{s.deadline && ` · bis ${fmtDate(s.deadline)}`}
                  </p>
                </div>
                <ChevronRight className="h-4 w-4 text-muted-foreground" />
              </Link>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Dokumente */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileText className="h-5 w-5 text-primary" /> Dokumente ({dokumente.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {dokumente.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Aktuell sind keine Dokumente für Sie freigegeben.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
              {dokumente.map((d) => (
                <Link key={d.id} to={`/portal/dokument/${d.id}`}
                  data-testid={`portal-dok-${d.id}`}
                  className="flex items-center gap-3 rounded-lg border bg-card p-3 hover:bg-secondary/50 transition-colors">
                  <FileText className="h-8 w-8 text-primary shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{d.titel}</p>
                      {istNeu(d.erstellt_am, d.neu_badge_tage) && (
                        <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">Neu</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground truncate">{d.beschreibung || d.dateiname}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">{fmtDate((d.erstellt_am || "").slice(0, 10))}</p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Galerien */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="flex items-center gap-2 text-base">
            <Images className="h-5 w-5 text-primary" /> Galerien ({gallerien.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          {gallerien.length === 0 ? (
            <p className="text-sm text-muted-foreground py-6 text-center">Keine Galerien freigegeben.</p>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
              {gallerien.map((g) => (
                <Link key={g.id} to={`/portal/galerie/${g.id}`}
                  data-testid={`portal-gal-${g.id}`}
                  className="rounded-lg border bg-card overflow-hidden hover:shadow-md transition">
                  <div className="aspect-video bg-secondary flex items-center justify-center">
                    {g.titelbild_photo_id ? (
                      <img
                        src={`${process.env.REACT_APP_BACKEND_URL}/api/gallery-photos/${g.titelbild_photo_id}/thumb`}
                        alt={g.titel}
                        className="w-full h-full object-cover" />
                    ) : (
                      <Images className="h-10 w-10 text-muted-foreground" />
                    )}
                  </div>
                  <div className="p-3">
                    <div className="flex items-center gap-2">
                      <p className="font-medium truncate">{g.titel}</p>
                      {istNeu(g.erstellt_am, g.neu_badge_tage) && (
                        <Badge variant="outline" className="text-[10px] bg-primary/10 text-primary border-primary/30">Neu</Badge>
                      )}
                    </div>
                    <p className="text-xs text-muted-foreground">{g.foto_count || 0} Fotos</p>
                  </div>
                </Link>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
